import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PpeIssuanceRecord, PpeNorm } from '@/entities/ppe/model/ppeSchema';
import { mockDelay } from '@/shared/api/mockDelay';
import { appendJsonArray, readJsonArray } from '@/shared/api/queryStorage';
import { buildPpeNormDetail, ppeNorms, ppeIssuanceRecords, ppeProcurement } from './mockData';

const PPE_NORMS_EXTRA_KEY = 'ohs-arm-ppe-norms-extra';
const PPE_NORMS_HISTORY_KEY = 'ohs-arm-ppe-norms-history';
const PPE_ISSUANCE_EXTRA_KEY = 'ohs-arm-ppe-issuance-extra';

function mergePpeNormsFromStorage(): PpeNorm[] {
  return [...ppeNorms, ...readJsonArray<PpeNorm>(PPE_NORMS_EXTRA_KEY)];
}

/** GET /api/ppe/norms */
export function usePpeNorms() {
  return useQuery({
    queryKey: ['ppe', 'norms'],
    queryFn: () => mockDelay(mergePpeNormsFromStorage()),
  });
}

/** GET /api/ppe/norms/:id */
export function usePpeNorm(id: number) {
  return useQuery({
    queryKey: ['ppe', 'norms', 'detail', id],
    queryFn: async () => {
      const merged = mergePpeNormsFromStorage();
      const norm = merged.find((r) => r.id === id);
      if (!norm) return null;
      return mockDelay(buildPpeNormDetail(norm));
    },
    enabled: Number.isFinite(id) && id > 0,
  });
}

/** POST /api/ppe/norms */
export function useAddPpeNorm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<PpeNorm, 'id'>) => {
      const id = Date.now();
      const row = { id, ...input };
      appendJsonArray(PPE_NORMS_EXTRA_KEY, row);
      appendJsonArray(PPE_NORMS_HISTORY_KEY, {
        date: new Date().toLocaleString('ru-RU'),
        action: `Добавлена норма: ${row.ppeName} для ${row.position} (${row.quantity} / ${row.period})`,
      });
      await mockDelay(null);
      return row;
    },
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: ['ppe'] });
      void queryClient.invalidateQueries({ queryKey: ['ppe', 'norms', 'detail', row.id] });
    },
  });
}

/** GET /api/ppe/norms/history */
export function usePpeNormsHistory() {
  return useQuery({
    queryKey: ['ppe', 'norms', 'history'],
    queryFn: () => mockDelay(readJsonArray<{ date: string; action: string }>(PPE_NORMS_HISTORY_KEY)),
  });
}

/** GET /api/ppe/issuance */
export function usePpeIssuanceRecords() {
  return useQuery({
    queryKey: ['ppe', 'issuance'],
    queryFn: () => mockDelay([...ppeIssuanceRecords, ...readJsonArray<PpeIssuanceRecord>(PPE_ISSUANCE_EXTRA_KEY)]),
  });
}

/** POST /api/ppe/issuance */
export function useAddPpeIssuance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<PpeIssuanceRecord, 'id'>) => {
      const id = Date.now();
      const row: PpeIssuanceRecord = { id, ...input };
      appendJsonArray(PPE_ISSUANCE_EXTRA_KEY, row);
      appendJsonArray(PPE_NORMS_HISTORY_KEY, {
        date: new Date().toLocaleString('ru-RU'),
        action: `Выдача СИЗ: ${row.ppeName} × ${row.quantity} — ${row.employeeName}`,
      });
      await mockDelay(null);
      return row;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ppe'] });
    },
  });
}

/** GET /api/ppe/procurement */
export function usePpeProcurement() {
  return useQuery({
    queryKey: ['ppe', 'procurement'],
    queryFn: () => mockDelay(ppeProcurement),
  });
}
